import {
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  NgModule,
  OnDestroy,
  Output,
  QueryList,
} from '@angular/core';
import { AccordionTabComponent, SystemAccordionTabModule } from './components';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';

export interface AccordionTabOpenEvent {
  /**
   * Browser event.
   */
  originalEvent: Event;
  /**
   * Opened tab index.
   */
  index: number;
}

export interface AccordionTabCloseEvent extends AccordionTabOpenEvent {}

@Component({
  selector: 'app-system-accordion',
  templateUrl: './accordion.component.html',
  styleUrl: './accordion.component.scss',
})
export class AccordionComponent implements OnDestroy {
  /**
   * Inline style of the tab header and content.
   * @group Props
   */
  @Input() style: { [klass: string]: any } | null | undefined;
  /**
   * Class of the element.
   * @group Props
   */
  @Input() styleClass!: string;
  /**
   * Index of the active tab or an array of indexes in multiple mode.
   * @group Props
   */
  @Input() get activeIndex(): number | number[] | null | undefined {
    return this._activeIndex;
  }
  set activeIndex(val: number | number[] | null | undefined) {
    this._activeIndex = val;
    if (this.preventActiveIndexPropagation) {
      this.preventActiveIndexPropagation = false;

      return;
    }

    this.updateSelectionState();
  }
  /**
   * The aria-level that each accordion header will have. The default value is 2 as per W3C specifications
   * @group Props
   */
  @Input() get headerAriaLevel(): number {
    return this._headerAriaLevel;
  }
  set headerAriaLevel(val: number) {
    if (typeof val === 'number' && val > 0) {
      this._headerAriaLevel = val;
    } else if (this._headerAriaLevel !== 2) {
      this._headerAriaLevel = 2;
    }
  }
  /**
   * Callback to invoke when an active tab is collapsed by clicking on the header.
   * @param {AccordionTabCloseEvent} event - Custom tab close event.
   * @group Emits
   */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onClose: EventEmitter<AccordionTabCloseEvent> = new EventEmitter();
  /**
   * Callback to invoke when a tab gets expanded.
   * @param {AccordionTabOpenEvent} event - Custom tab open event.
   * @group Emits
   */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onOpen: EventEmitter<AccordionTabOpenEvent> = new EventEmitter();
  /**
   * Returns the active index.
   * @param {number | number[]} value - New index.
   * @group Emits
   */
  @Output() activeIndexChange: EventEmitter<number | number[]> = new EventEmitter<number | number[]>();

  @ContentChildren(AccordionTabComponent, { descendants: true }) tabList: QueryList<AccordionTabComponent> | undefined;

  tabListSubscription: Subscription | null = null;
  multiple = true;
  preventActiveIndexPropagation = false;
  tabs: AccordionTabComponent[] = [];

  private _activeIndex: any;
  private _headerAriaLevel = 2;

  constructor(
    public el: ElementRef,
    public changeDetector: ChangeDetectorRef
  ) {}

  ngAfterContentInit() {
    this.initTabs();

    this.tabListSubscription = (this.tabList as QueryList<AccordionTabComponent>).changes.subscribe(() => {
      this.initTabs();
    });
  }

  ngOnDestroy() {
    if (this.tabListSubscription) {
      this.tabListSubscription.unsubscribe();
    }
  }

  initTabs() {
    this.tabs = (this.tabList as QueryList<AccordionTabComponent>).toArray();

    this.tabs.forEach(tab => {
      tab.headerAriaLevel = this._headerAriaLevel;
    });

    this.updateSelectionState();
    this.changeDetector.markForCheck();
  }

  updateSelectionState() {
    if (this.tabs && this.tabs.length && this._activeIndex) {
      for (let i = 0; i < this.tabs.length; i++) {
        const selected = this.multiple ? this._activeIndex.includes(i) : i === this._activeIndex;
        const changed = selected !== this.tabs[i].selected;

        if (changed) {
          this.tabs[i].selected = selected;
          this.tabs[i].selectedChange.emit(selected);
          this.tabs[i].changeDetector.markForCheck();
        }
      }
    }
  }

  /**
   * Checks if a tab at the given index is active.
   *
   * @param {any} index - The index of the tab to check.
   * @returns {boolean} - Returns true if the tab at the given index is active, otherwise false.
   *
   * If the `multiple` property is true, it checks if the `_activeIndex` array includes the given index.
   * If the `multiple` property is false, it checks if the `_activeIndex` is equal to the given index.
   */
  isTabActive(index: any) {
    return this.multiple
      ? this._activeIndex && (<number[]>this._activeIndex).includes(index)
      : this._activeIndex === index;
  }

  /**
   * Retrieves a property value from a tab object.
   *
   * @param {any} tab - The tab object from which to retrieve the property.
   * @param {any} name - The name of the property to retrieve.
   * @returns {any} - Returns the value of the specified property if it exists, otherwise undefined.
   *
   * If the `tab` object has a `props` property, it returns the value of the property with the given `name`.
   * If the `tab` object does not have a `props` property, it returns undefined.
   */
  getTabProp(tab: any, name: any) {
    return tab.props ? tab.props[name] : undefined;
  }

  updateActiveIndex() {
    let index: number | number[] | null = this.multiple ? [] : null;
    this.tabs.forEach((tab, i) => {
      if (tab.selected) {
        if (this.multiple) {
          (index as number[]).push(i);
        } else {
          index = i;

          return;
        }
      }
    });
    this.preventActiveIndexPropagation = true;
    this._activeIndex = index;
    this.activeIndexChange.emit(index as number[] | number);
  }
}

@NgModule({
  declarations: [AccordionComponent],
  imports: [CommonModule],
  exports: [AccordionComponent, SystemAccordionTabModule],
})
export class SystemAccordionModule {}
